/**
 * Main/backup at a glance (PLAN §8.6; wave 7b-tail C): per site, every node —
 * its place in the pair, online, the peer link, its witnesses, what it runs
 * active and standby — and every production, worst first, with its grade and
 * reason, its active and standby node, the standby's stream (lag behind the
 * active, last snapshot, gaps, resyncs) and the active's acks.
 *
 * Actions, as the view allows them per production (`lib/redundancy.ts`'
 * `pairActions`): switch over; manual only on/off (node); force the standby on
 * air (node, the emergency activation — the view confirms it); and "switch
 * whole node" on the node the page talks to. Props in, events out.
 */
import type { SitePairNodeWire, SitePairProductionWire, SiteRedundancyWire } from '@acp/api-contract/pairStatus.js';
import { RefreshCwIcon } from 'lucide-react';
import { acksText, durationText, type PairActions, streamText } from '../../lib/redundancy.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';
import { GradeBadge } from './GradeBadge.js';

export type PairOverviewProps = {
  readonly sites: readonly SiteRedundancyWire[];
  readonly loading: boolean;
  readonly error: string | null;
  /** What the viewer may do to each production here, by production id (absent: nothing). */
  readonly actions: Readonly<Record<string, PairActions>>;
  /** The node the page talks to (its "switch whole node"), or null in the cloud. */
  readonly selfNodeId: string | null;
  readonly canSwitchoverAll: boolean;
  /** A production id (or `all`) whose operation is under way. */
  readonly pending: string | null;
  readonly onRetry: () => void;
  readonly onOpen: (productionId: string) => void;
  readonly onSwitchover: (productionId: string) => void;
  readonly onManualOnly: (productionId: string, manualOnly: boolean) => void;
  readonly onForcePromote: (productionId: string) => void;
  readonly onSwitchoverAll: () => void;
};

const NONE: PairActions = { switchover: false, manualOnly: false, forcePromote: false };

function nodeLabel(nodes: readonly SitePairNodeWire[], nodeId: string | null): string {
  if (nodeId === null) return '–';
  return nodes.find((n) => n.nodeId === nodeId)?.label ?? nodeId;
}

function NodeRow({ node: n, switchAll, pending, onSwitchoverAll }: { readonly node: SitePairNodeWire; readonly switchAll: boolean } & Pick<PairOverviewProps, 'pending' | 'onSwitchoverAll'>) {
  return (
    <TableRow data-testid="pair-node" data-node={n.nodeId} data-online={n.online ? 'yes' : 'no'}>
      <TableCell>
        <div className="font-medium">{n.label}</div>
        <div className="font-mono text-xs text-muted-foreground">
          {n.nodeId} · {n.nodeRole ?? 'no pair'}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={n.online ? 'success' : 'offline'}>{n.online ? 'online' : 'offline'}</Badge>
        {n.reportedAgoMs === null || n.reportedAgoMs === 0 ? null : <div className="text-xs text-muted-foreground">reported {durationText(n.reportedAgoMs)} ago</div>}
      </TableCell>
      <TableCell className="text-sm">
        <div>{n.peerLink === null ? '–' : `peer ${n.peerLink}`}</div>
        <div className="text-xs text-muted-foreground">
          {n.witness === null ? 'no witness' : `witness ${n.witness.mode}`}
          {n.witnesses.map((w) => ` · ${w.kind} ${w.reachable ? 'reachable' : 'UNREACHABLE'}`).join('')}
        </div>
      </TableCell>
      <TableCell>{String(n.active.length)}</TableCell>
      <TableCell>{String(n.standby.length)}</TableCell>
      <TableCell className="text-right">
        {switchAll ? (
          <Button size="sm" variant="outline" disabled={pending !== null} onClick={onSwitchoverAll} data-testid="switchover-all">
            {pending === 'all' ? 'Switching…' : 'Switch whole node'}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function NodesTable({ site, selfNodeId, canSwitchoverAll, pending, onSwitchoverAll }: { readonly site: SiteRedundancyWire } & Pick<PairOverviewProps, 'selfNodeId' | 'canSwitchoverAll' | 'pending' | 'onSwitchoverAll'>) {
  return (
    <Table data-testid="pair-nodes">
      <TableHeader>
        <TableRow>
          <TableHead>Node</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Peer link · witness</TableHead>
          <TableHead>Active</TableHead>
          <TableHead>Standby</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {site.nodes.map((n) => (
          <NodeRow key={n.nodeId} node={n} switchAll={n.nodeId === selfNodeId && canSwitchoverAll && n.active.length > 0} pending={pending} onSwitchoverAll={onSwitchoverAll} />
        ))}
      </TableBody>
    </Table>
  );
}

type RowProps = { readonly row: SitePairProductionWire; readonly nodes: readonly SitePairNodeWire[]; readonly actions: PairActions } & Pick<PairOverviewProps, 'pending' | 'onOpen' | 'onSwitchover' | 'onManualOnly' | 'onForcePromote'>;

function RowActions({ row, actions, pending, onSwitchover, onManualOnly, onForcePromote }: Omit<RowProps, 'nodes' | 'onOpen'>) {
  const busy = pending !== null;
  return (
    <div className="flex flex-wrap justify-end gap-1">
      {actions.forcePromote ? (
        <Button size="sm" variant="destructive" disabled={busy} onClick={() => onForcePromote(row.productionId)} data-testid="force-promote">
          Force standby on air…
        </Button>
      ) : null}
      {actions.switchover ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onSwitchover(row.productionId)} data-testid="pair-switchover">
          {pending === row.productionId ? 'Working…' : 'Switch over'}
        </Button>
      ) : null}
      {actions.manualOnly ? (
        <Button size="sm" variant="ghost" disabled={busy} aria-pressed={row.manualOnly} onClick={() => onManualOnly(row.productionId, !row.manualOnly)} data-testid="manual-only">
          {row.manualOnly ? 'Manual only: on' : 'Manual only: off'}
        </Button>
      ) : null}
    </div>
  );
}

function ProductionRow({ row, nodes, actions, pending, onOpen, onSwitchover, onManualOnly, onForcePromote }: RowProps) {
  const stream = streamText(row.stream);
  const acks = acksText(row.acks);
  return (
    <TableRow data-testid="pair-production" data-production={row.productionId} data-grade={row.grade}>
      <TableCell>
        <GradeBadge grade={row.grade} reason={row.reason} />
      </TableCell>
      <TableCell>
        <Button variant="link" className="h-auto p-0 font-medium" onClick={() => onOpen(row.productionId)}>
          {row.name ?? row.productionId}
        </Button>
        <div className="text-xs text-muted-foreground">
          term {String(row.term)}
          {row.manualOnly ? ' · manual failover only' : ''}
        </div>
      </TableCell>
      <TableCell className="text-sm">
        <div>active {nodeLabel(nodes, row.activeNode)}</div>
        <div className="text-muted-foreground">standby {nodeLabel(nodes, row.standbyNode)}</div>
      </TableCell>
      <TableCell className="max-w-sm text-sm">
        <div data-testid="pair-detail">{row.detail}</div>
        {row.outOfSyncMs === null ? null : (
          <div className="text-warning" data-testid="out-of-sync">
            out of sync for {durationText(row.outOfSyncMs)}
          </div>
        )}
        {stream === null ? null : <div className="text-xs text-muted-foreground">stream: {stream}</div>}
        {acks === null ? null : <div className="text-xs text-muted-foreground">acks: {acks}</div>}
        {row.alarm === null ? null : (
          <div className="text-xs text-destructive" role="alert">
            {row.alarm}
          </div>
        )}
      </TableCell>
      <TableCell>
        <RowActions row={row} actions={actions} pending={pending} onSwitchover={onSwitchover} onManualOnly={onManualOnly} onForcePromote={onForcePromote} />
      </TableCell>
    </TableRow>
  );
}

function SiteCard(props: { readonly site: SiteRedundancyWire } & Omit<PairOverviewProps, 'sites' | 'loading' | 'error' | 'onRetry'>) {
  const { site } = props;
  return (
    <Card data-testid="pair-site" data-site={site.siteId}>
      <CardHeader>
        <CardTitle>{site.siteName ?? site.siteId}</CardTitle>
        <CardDescription>{site.source === 'node' ? 'Live, from this node and its peer.' : 'From the nodes’ last reports (every 30 s).'}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <NodesTable site={site} selfNodeId={props.selfNodeId} canSwitchoverAll={props.canSwitchoverAll} pending={props.pending} onSwitchoverAll={props.onSwitchoverAll} />
        {site.productions.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No productions on this site.</p>
        ) : (
          <Table data-testid="pair-productions">
            <TableHeader>
              <TableRow>
                <TableHead>Grade</TableHead>
                <TableHead>Production</TableHead>
                <TableHead>Nodes</TableHead>
                <TableHead>Why</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {site.productions.map((row) => (
                <ProductionRow
                  key={row.productionId}
                  row={row}
                  nodes={site.nodes}
                  actions={props.actions[row.productionId] ?? NONE}
                  pending={props.pending}
                  onOpen={props.onOpen}
                  onSwitchover={props.onSwitchover}
                  onManualOnly={props.onManualOnly}
                  onForcePromote={props.onForcePromote}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export function PairOverview(props: PairOverviewProps) {
  const { sites, loading, error, onRetry } = props;
  return (
    <div className="grid gap-4" data-testid="pair-overview">
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load the main/backup overview: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {loading && sites.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p> : null}
      {!loading && error === null && sites.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No sites with nodes to show.</p> : null}
      {sites.map((site) => (
        <SiteCard key={site.siteId} site={site} {...props} />
      ))}
    </div>
  );
}
